/**
 * Automation Lane Component
 * Visual editor for automation curves on tracks
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { DropdownPortal } from '../ui/dropdown-portal';
import {
  ChevronDown,
  Trash2,
  Eye,
  EyeOff,
  Plus,
  Plug,
  Copy,
  Clipboard,
  Sparkles,
} from 'lucide-react';
import { conditionalSnap } from './GridSnap';
import {
  AUTOMATION_PARAMS,
  CURVE_TYPES,
  createAutomationPoint,
  addPointToLane,
  removePointFromLane,
  updatePointInLane,
  normalizeValue,
  denormalizeValue,
  formatValue,
  snapTimeToGrid,
  getParamConfig,
  getTrackAutomationParams,
  parseFullPluginParamKey,
  createPluginParamKey,
} from './Automation';

const LANE_HEIGHT = 60;
const POINT_RADIUS = 6;

// Automation presets for quick curves
const AUTOMATION_PRESETS = [
  { 
    id: 'fade-in', 
    name: 'Fade In', 
    description: 'Volume ramp from 0 to max',
    points: [
      { timePercent: 0, value: 0 },
      { timePercent: 1, value: 1 },
    ],
    curveType: CURVE_TYPES.EXPONENTIAL,
  },
  {
    id: 'fade-out',
    name: 'Fade Out',
    description: 'Volume ramp from max to 0',
    points: [
      { timePercent: 0, value: 1 },
      { timePercent: 1, value: 0 },
    ],
    curveType: CURVE_TYPES.EXPONENTIAL,
  },
  {
    id: 'swell',
    name: 'Swell',
    description: 'Volume builds then drops',
    points: [
      { timePercent: 0, value: 0.2 },
      { timePercent: 0.5, value: 1 },
      { timePercent: 1, value: 0.2 },
    ],
    curveType: CURVE_TYPES.EXPONENTIAL,
  },
  {
    id: 'lfo',
    name: 'LFO (Tremolo)',
    description: 'Rhythmic pulsing effect',
    points: [
      { timePercent: 0, value: 1 },
      { timePercent: 0.125, value: 0.3 },
      { timePercent: 0.25, value: 1 },
      { timePercent: 0.375, value: 0.3 },
      { timePercent: 0.5, value: 1 },
      { timePercent: 0.625, value: 0.3 },
      { timePercent: 0.75, value: 1 },
      { timePercent: 0.875, value: 0.3 },
      { timePercent: 1, value: 1 },
    ],
    curveType: CURVE_TYPES.EXPONENTIAL,
  },
  {
    id: 'pan-sweep',
    name: 'Pan Sweep L→R',
    description: 'Smooth pan from left to right',
    points: [
      { timePercent: 0, value: 0 },
      { timePercent: 0.5, value: 0.5 },
      { timePercent: 1, value: 1 },
    ],
    curveType: CURVE_TYPES.LINEAR,
  },
  {
    id: 'ramp-up',
    name: 'Ramp Up',
    description: 'Linear increase',
    points: [
      { timePercent: 0, value: 0 },
      { timePercent: 1, value: 1 },
    ],
    curveType: CURVE_TYPES.LINEAR,
  },
];

export const AutomationLaneView = ({
  lane,
  trackColor,
  trackPlugins = [], // Track's plugin chain for parameter discovery
  totalDuration,
  bpm,
  gridSize,
  snapEnabled,
  isSelected,
  currentTime,
  isPlaying,
  onLaneUpdate,
  onDelete,
  onToggleVisibility,
}) => {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [showParamDropdown, setShowParamDropdown] = useState(false);
  const [showCurveTypeMenu, setShowCurveTypeMenu] = useState(null); // Point ID or null
  const [showPresetMenu, setShowPresetMenu] = useState(false);
  const [copiedPoints, setCopiedPoints] = useState(null);
  const containerRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  const pendingClickRef = useRef(null);
  const laneRef = useRef(lane); // Ref to track latest lane state
  const paramBtnRef = useRef(null);
  const presetBtnRef = useRef(null);
  
  // Keep laneRef in sync with lane prop
  useEffect(() => {
    laneRef.current = lane;
  }, [lane]);
  
  // Get param config - supports both track params and plugin params
  const paramConfig = useMemo(() => {
    return getParamConfig(lane.parameter) || AUTOMATION_PARAMS[lane.parameter];
  }, [lane.parameter]);
  
  // Get available automation params (track + plugin)
  const availableParams = useMemo(() => {
    return getTrackAutomationParams(trackPlugins);
  }, [trackPlugins]);
  
  // Curve type display labels
  const curveTypeLabels = {
    [CURVE_TYPES.LINEAR]: { label: 'Lin', icon: '/' },
    [CURVE_TYPES.EXPONENTIAL]: { label: 'Exp', icon: '⌒' },
    [CURVE_TYPES.HOLD]: { label: 'Hold', icon: '⌐' },
  };
  
  // SVG viewBox dimensions for consistent coordinate system
  const SVG_WIDTH = 1000; // Use fixed viewBox width for path calculations
  
  // Convert time to X position (viewBox units for SVG paths)
  const timeToX = useCallback((time) => {
    return (time / totalDuration) * SVG_WIDTH;
  }, [totalDuration]);
  
  // Convert time to percentage (for CSS positioning of DOM elements)
  const timeToPercent = useCallback((time) => {
    return (time / totalDuration) * 100;
  }, [totalDuration]);
  
  // Convert X position to time
  const xToTime = useCallback((x, containerWidth) => {
    const time = (x / containerWidth) * totalDuration;
    return snapEnabled ? snapTimeToGrid(time, gridSize) : time;
  }, [totalDuration, snapEnabled, gridSize]);
  
  // Convert value to Y position (0 at top, LANE_HEIGHT at bottom)
  const valueToY = useCallback((value) => {
    const normalized = normalizeValue(value, paramConfig);
    return LANE_HEIGHT - (normalized * (LANE_HEIGHT - 10)) - 5; // 5px padding top/bottom
  }, [paramConfig]);
  
  // Convert Y position to value
  const yToValue = useCallback((y) => {
    const normalized = Math.max(0, Math.min(1, (LANE_HEIGHT - y - 5) / (LANE_HEIGHT - 10)));
    return denormalizeValue(normalized, paramConfig);
  }, [paramConfig]);
  
  // Generate SVG path for the automation curve
  const generatePath = useCallback(() => {
    if (!lane.points || lane.points.length === 0) return '';
    
    const sortedPoints = [...lane.points].sort((a, b) => a.time - b.time);
    
    let path = '';
    
    // Start from left edge at first point's value
    const firstY = valueToY(sortedPoints[0].value);
    path += `M 0 ${firstY}`;
    path += ` L ${timeToX(sortedPoints[0].time)} ${firstY}`;
    
    // Draw curve through all points
    for (let i = 0; i < sortedPoints.length; i++) {
      const point = sortedPoints[i];
      const x = timeToX(point.time);
      const y = valueToY(point.value);
      
      if (i > 0) {
        const prevPoint = sortedPoints[i - 1];
        const prevX = timeToX(prevPoint.time);
        const prevY = valueToY(prevPoint.value);
        
        switch (prevPoint.curveType) {
          case CURVE_TYPES.HOLD:
            // Step function - horizontal then vertical
            path += ` L ${x} ${prevY} L ${x} ${y}`;
            break;
          case CURVE_TYPES.EXPONENTIAL:
            // Exponential curve using quadratic bezier
            // Control point creates an exponential-like curve
            const cpX = prevX + (x - prevX) * 0.8; // Control point at 80% across
            path += ` Q ${cpX} ${prevY} ${x} ${y}`;
            break;
          case CURVE_TYPES.LINEAR:
          default:
            // Simple linear line
            path += ` L ${x} ${y}`;
            break;
        }
      } else {
        path += ` L ${x} ${y}`;
      }
    }
    
    // Extend to right edge
    const lastPoint = sortedPoints[sortedPoints.length - 1];
    const lastY = valueToY(lastPoint.value);
    path += ` L ${SVG_WIDTH} ${lastY}`;
    
    return path;
  }, [lane.points, timeToX, valueToY]);
  
  // Handle click on lane to add point (with delay to allow double-click detection)
  const handleLaneClick = useCallback((e) => {
    e.stopPropagation(); // Prevent click from bubbling to parent (timeline seek)
    if (isDragging) return;
    
    // Cancel any pending click
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    // Store the click data
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    pendingClickRef.current = { x, y, rect };
    
    // Delay adding point to allow double-click detection
    clickTimeoutRef.current = setTimeout(() => {
      if (!pendingClickRef.current) return;
      
      const { x, y, rect } = pendingClickRef.current;
      const time = xToTime(x, rect.width);
      const value = yToValue(y);
      
      // Use ref to get latest lane state (avoids stale closure)
      const currentLane = laneRef.current;
      
      // Minimum spacing validation (0.1 seconds between points)
      const MIN_SPACING = 0.1;
      const tooClose = currentLane.points.some(p => Math.abs(p.time - time) < MIN_SPACING);
      if (tooClose) {
        pendingClickRef.current = null;
        return; // Don't add point too close to existing one
      }
      
      // Apply grid snapping if enabled
      const snappedTime = conditionalSnap(time, snapEnabled, bpm);
      
      // Re-check spacing after snap (prevent collapse onto same timestamp)
      const tooCloseAfterSnap = currentLane.points.some(p => Math.abs(p.time - snappedTime) < MIN_SPACING);
      if (tooCloseAfterSnap) {
        pendingClickRef.current = null;
        return;
      }
      
      const newPoint = createAutomationPoint({
        time: Math.max(0, Math.min(totalDuration, snappedTime)),
        value: Math.max(paramConfig.min, Math.min(paramConfig.max, value)),
      });
      
      const updatedLane = addPointToLane(currentLane, newPoint);
      onLaneUpdate?.(updatedLane, { type: 'ADD_POINT', point: newPoint });
      setSelectedPoint(newPoint.id);
      
      pendingClickRef.current = null;
    }, 200); // 200ms delay to distinguish single click from double-click
  }, [isDragging, xToTime, yToValue, totalDuration, paramConfig, onLaneUpdate, snapEnabled, bpm]);
  
  // Handle point mouse down
  const handlePointMouseDown = useCallback((e, point) => {
    e.stopPropagation();
    setSelectedPoint(point.id);
    setIsDragging(true);
    
    const rect = containerRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      time: point.time,
      value: point.value,
      containerWidth: rect.width,
    });
  }, []);
  
  // Handle mouse move for dragging
  useEffect(() => {
    if (!isDragging || !dragStart || !selectedPoint) return;
    
    const MIN_SPACING = 0.1;
    
    const handleMouseMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      let newTime = xToTime(x, rect.width);
      let newValue = yToValue(y);
      
      // Clamp values
      newTime = Math.max(0, Math.min(totalDuration, newTime));
      newValue = Math.max(paramConfig.min, Math.min(paramConfig.max, newValue));
      
      const updatedLane = updatePointInLane(lane, selectedPoint, {
        time: newTime,
        value: newValue,
      });
      
      onLaneUpdate?.(updatedLane, { type: 'MOVE_POINT', pointId: selectedPoint, skipUndo: true });
    };
    
    const handleMouseUp = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) {
        setIsDragging(false);
        setDragStart(null);
        return;
      }
      
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      let newTime = xToTime(x, rect.width);
      let newValue = yToValue(y);
      
      newTime = Math.max(0, Math.min(totalDuration, newTime));
      newValue = Math.max(paramConfig.min, Math.min(paramConfig.max, newValue));
      
      // Apply grid snapping on mouse up (final position)
      const snappedTime = conditionalSnap(newTime, snapEnabled, bpm);
      
      // Check if snapped position would overlap with another point
      const currentLane = laneRef.current;
      const wouldOverlap = currentLane.points.some(
        p => p.id !== selectedPoint && Math.abs(p.time - snappedTime) < MIN_SPACING
      );
      
      // Use snapped time if it doesn't cause overlap, otherwise use raw time
      const finalTime = wouldOverlap ? newTime : snappedTime;
      
      const updatedLane = updatePointInLane(lane, selectedPoint, {
        time: finalTime,
        value: newValue,
      });
      
      onLaneUpdate?.(updatedLane, {
        type: 'MOVE_POINT',
        pointId: selectedPoint,
        oldTime: dragStart.time,
        oldValue: dragStart.value,
      });
      
      setIsDragging(false);
      setDragStart(null);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, selectedPoint, lane, xToTime, yToValue, totalDuration, paramConfig, onLaneUpdate, snapEnabled, bpm]);
  
  // Handle point double-click to delete
  const handlePointDoubleClick = useCallback((e, point) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Cancel any pending single-click to add point
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    pendingClickRef.current = null;
    
    // Don't delete if it's the only point
    if (lane.points.length <= 1) return;
    
    const updatedLane = removePointFromLane(lane, point.id);
    onLaneUpdate?.(updatedLane, { type: 'DELETE_POINT', point });
    setSelectedPoint(null);
  }, [lane, onLaneUpdate]);
  
  // Handle keyboard for selected point
  useEffect(() => {
    if (!selectedPoint) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (lane.points.length > 1) {
          const point = lane.points.find(p => p.id === selectedPoint);
          if (point) {
            const updatedLane = removePointFromLane(lane, selectedPoint);
            onLaneUpdate?.(updatedLane, { type: 'DELETE_POINT', point });
            setSelectedPoint(null);
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPoint, lane, onLaneUpdate]);
  
  // Cleanup click timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);
  
  // Copy all points to clipboard
  const handleCopyPoints = useCallback(() => {
    setCopiedPoints({
      points: lane.points.map(p => ({
        timePercent: p.time / totalDuration,
        value: normalizeValue(p.value, paramConfig),
        curveType: p.curveType,
      })),
      parameter: lane.parameter,
    });
  }, [lane, totalDuration, paramConfig]);
  
  // Paste points from clipboard
  const handlePastePoints = useCallback(() => {
    if (!copiedPoints) return;
    
    const newPoints = copiedPoints.points.map((p, i) => createAutomationPoint(
      p.timePercent * totalDuration,
      denormalizeValue(p.value, paramConfig),
      p.curveType || CURVE_TYPES.LINEAR
    ));
    
    const updatedLane = {
      ...lane,
      points: newPoints,
    };
    
    onLaneUpdate?.(updatedLane, { type: 'PASTE_POINTS' });
  }, [copiedPoints, totalDuration, paramConfig, lane, onLaneUpdate]);
  
  // Apply automation preset
  const handleApplyPreset = useCallback((preset) => {
    const newPoints = preset.points.map((p, i) => createAutomationPoint(
      p.timePercent * totalDuration,
      denormalizeValue(p.value, paramConfig),
      preset.curveType || CURVE_TYPES.LINEAR
    ));
    
    const updatedLane = {
      ...lane,
      points: newPoints,
    };
    
    onLaneUpdate?.(updatedLane, { type: 'APPLY_PRESET', preset: preset.name });
    setShowPresetMenu(false);
  }, [totalDuration, paramConfig, lane, onLaneUpdate]);
  
  // Clear all points (reset to default)
  
  // Change curve type for a point
  const handleCurveTypeChange = useCallback((pointId, newCurveType) => {
    const updatedLane = updatePointInLane(lane, pointId, { curveType: newCurveType });
    onLaneUpdate?.(updatedLane, { type: 'CHANGE_CURVE_TYPE', pointId, newCurveType });
    setShowCurveTypeMenu(null);
  }, [lane, onLaneUpdate]);
  
  // Cycle curve type on right-click
  const handlePointContextMenu = useCallback((e, point) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Cycle through curve types: LINEAR -> EXPONENTIAL -> HOLD -> LINEAR
    const curveOrder = [CURVE_TYPES.LINEAR, CURVE_TYPES.EXPONENTIAL, CURVE_TYPES.HOLD];
    const currentIndex = curveOrder.indexOf(point.curveType);
    const nextIndex = (currentIndex + 1) % curveOrder.length;
    const newCurveType = curveOrder[nextIndex];
    
    handleCurveTypeChange(point.id, newCurveType);
  }, [handleCurveTypeChange]);
  
  // Change parameter (supports both track and plugin params)
  const handleParameterChange = useCallback((newParam) => {
    const newConfig = getParamConfig(newParam) || AUTOMATION_PARAMS[newParam];
    if (!newConfig) return;
    
    const updatedLane = {
      ...lane,
      parameter: newParam,
      // Reset points to default value for new parameter
      points: lane.points.map(p => ({
        ...p,
        value: newConfig.default,
      })),
    };
    onLaneUpdate?.(updatedLane, { type: 'CHANGE_PARAMETER', oldParameter: lane.parameter });
    setShowParamDropdown(false);
  }, [lane, onLaneUpdate]);
  
  // Get display label for current parameter
  const getParamDisplayLabel = useCallback(() => {
    if (!paramConfig) return lane.parameter;
    
    // Check if it's a plugin parameter with instance ID
    if (lane.parameter.includes(':')) {
      const parsed = parseFullPluginParamKey(lane.parameter);
      // Find plugin name
      const plugin = trackPlugins.find(p => p.id === parsed.pluginId);
      const pluginName = plugin?.name || parsed.pluginType;
      const paramLabel = paramConfig.label.replace(/^[^:]+:\s*/, ''); // Remove prefix
      return `${pluginName}: ${paramLabel}`;
    }
    
    return paramConfig.label;
  }, [lane.parameter, paramConfig, trackPlugins]);
  
  // Check if parameter is a plugin parameter
  const isPluginParam = lane.parameter.includes(':');
  
  // Get current playhead value
  const currentValue = lane.points?.length > 0 
    ? (() => {
        const sortedPoints = [...lane.points].sort((a, b) => a.time - b.time);
        // Find value at currentTime using linear interpolation
        if (currentTime <= sortedPoints[0].time) return sortedPoints[0].value;
        if (currentTime >= sortedPoints[sortedPoints.length - 1].time) return sortedPoints[sortedPoints.length - 1].value;
        
        for (let i = 0; i < sortedPoints.length - 1; i++) {
          if (currentTime >= sortedPoints[i].time && currentTime < sortedPoints[i + 1].time) {
            const t = (currentTime - sortedPoints[i].time) / (sortedPoints[i + 1].time - sortedPoints[i].time);
            return sortedPoints[i].value + (sortedPoints[i + 1].value - sortedPoints[i].value) * t;
          }
        }
        return sortedPoints[0].value;
      })()
    : paramConfig?.default ?? 0;
  
  return (
    <div 
      className={`relative border-b border-zinc-800 ${isSelected ? 'bg-zinc-800/20' : 'bg-zinc-900/30'}`}
      style={{ height: LANE_HEIGHT + 24 }} // Extra for header
      data-testid={`automation-lane-${lane.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Lane Header */}
      <div 
        className="flex items-center justify-between px-2 py-1 bg-zinc-800/50 border-b border-zinc-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          {/* Parameter Selector */}
          <div className="relative">
            <Button
              ref={paramBtnRef}
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowParamDropdown(!showParamDropdown);
              }}
              className="h-6 px-2 text-xs gap-1"
            >
              {isPluginParam && <Plug className="w-3 h-3 text-violet-400" />}
              <span style={{ color: trackColor }}>{getParamDisplayLabel()}</span>
              <ChevronDown className="w-3 h-3" />
            </Button>
            
            <DropdownPortal
              triggerRef={paramBtnRef}
              isOpen={showParamDropdown}
              onClose={() => setShowParamDropdown(false)}
              width={160}
            >
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg min-w-[160px] max-h-[300px] overflow-y-auto">
                {/* Track Parameters Section */}
                <div className="px-2 py-1 text-[10px] font-medium text-zinc-500 bg-zinc-900/50 sticky top-0">
                  Track Parameters
                </div>
                {Object.entries(availableParams.track).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleParameterChange(key);
                    }}
                    className={`w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-700 ${
                      key === lane.parameter ? 'bg-zinc-700 text-white' : 'text-zinc-300'
                    }`}
                    data-testid={`param-select-${key}`}
                  >
                    {config.label}
                  </button>
                ))}
                
                {/* Plugin Parameters Sections */}
                {Object.entries(availableParams.plugins).map(([pluginKey, pluginData]) => (
                  <div key={pluginKey}>
                    <div className="px-2 py-1 text-[10px] font-medium text-violet-400 bg-zinc-900/50 sticky top-0 flex items-center gap-1 mt-1">
                      <Plug className="w-3 h-3" />
                      {pluginData.pluginName}
                    </div>
                    {Object.entries(pluginData.params).map(([paramKey, config]) => {
                      const fullKey = createPluginParamKey(pluginData.pluginId, pluginData.pluginType, paramKey.split('.')[1]);
                      const shortLabel = config.label.replace(/^[^:]+:\s*/, ''); // Remove prefix like "Comp: "
                      
                      return (
                        <button
                          key={fullKey}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleParameterChange(fullKey);
                          }}
                          className={`w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-700 pl-5 ${
                            fullKey === lane.parameter ? 'bg-zinc-700 text-white' : 'text-zinc-300'
                          }`}
                          data-testid={`param-select-${fullKey}`}
                        >
                          {shortLabel}
                        </button>
                      );
                    })}
                  </div>
                ))}
                
                {/* Show message if no plugins */}
                {Object.keys(availableParams.plugins).length === 0 && (
                  <div className="px-3 py-2 text-[10px] text-zinc-500 italic">
                    Add plugins to automate their parameters
                  </div>
                )}
              </div>
            </DropdownPortal>
          </div>
          
          {/* Current Value Display */}
          <span className="text-xs text-zinc-400 font-mono">
            {formatValue(currentValue, paramConfig)}
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Presets Button */}
          <div className="relative">
            <Button
              ref={presetBtnRef}
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowPresetMenu(!showPresetMenu);
              }}
              className="h-6 px-1.5"
              title="Apply Preset"
              data-testid="automation-presets-btn"
            >
              <Sparkles className="w-3 h-3" />
            </Button>
            
            <DropdownPortal
              triggerRef={presetBtnRef}
              isOpen={showPresetMenu}
              onClose={() => setShowPresetMenu(false)}
              align="right"
              width={150}
            >
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg min-w-[150px]">
                <div className="px-2 py-1 text-[10px] font-medium text-zinc-500 bg-zinc-900/50 border-b border-zinc-700">
                  Automation Presets
                </div>
                {AUTOMATION_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplyPreset(preset);
                    }}
                    className="w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-700 text-zinc-300"
                    title={preset.description}
                    data-testid={`preset-${preset.id}`}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </DropdownPortal>
          </div>
          
          {/* Copy Points */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyPoints();
            }}
            className="h-6 px-1.5"
            title="Copy automation curve"
            data-testid="automation-copy-btn"
          >
            <Copy className="w-3 h-3" />
          </Button>
          
          {/* Paste Points */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handlePastePoints();
            }}
            className={`h-6 px-1.5 ${!copiedPoints ? 'opacity-40' : ''}`}
            disabled={!copiedPoints}
            title="Paste automation curve"
            data-testid="automation-paste-btn"
          >
            <Clipboard className="w-3 h-3" />
          </Button>
          
          {/* Visibility Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility?.(lane.id);
            }}
            className="h-6 w-6 p-0"
            title={lane.isVisible ? 'Hide Lane' : 'Show Lane'}
          >
            {lane.isVisible ? (
              <Eye className="w-3 h-3" />
            ) : (
              <EyeOff className="w-3 h-3 text-zinc-500" />
            )}
          </Button>
          
          {/* Delete Lane */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(lane.id);
            }}
            className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
            title="Delete Lane"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
      
      {/* Automation Curve Area */}
      {lane.isVisible && (
        <div
          ref={containerRef}
          className="relative cursor-crosshair z-10"
          style={{ height: LANE_HEIGHT }}
          onClick={handleLaneClick}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Grid Lines (for reference) */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-0 right-0 border-t border-zinc-800/50" />
            <div className="absolute top-1/2 left-0 right-0 border-t border-zinc-700/50" />
            <div className="absolute top-3/4 left-0 right-0 border-t border-zinc-800/50" />
          </div>
          
          {/* SVG Curve */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox={`0 0 ${SVG_WIDTH} ${LANE_HEIGHT}`}
            preserveAspectRatio="none"
          >
            {/* Fill under curve */}
            <path
              d={`${generatePath()} L ${SVG_WIDTH} ${LANE_HEIGHT} L 0 ${LANE_HEIGHT} Z`}
              fill={trackColor || 'rgba(139, 92, 246, 0.15)'}
              style={{ opacity: 0.2 }}
            />
            {/* Curve line */}
            <path
              d={generatePath()}
              fill="none"
              stroke={trackColor || 'rgba(139, 92, 246, 0.8)'}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          
          {/* Automation Points */}
          {lane.points.map((point, index) => {
            const xPercent = timeToPercent(point.time);
            const y = valueToY(point.value);
            const isPointSelected = selectedPoint === point.id;
            const curveInfo = curveTypeLabels[point.curveType] || curveTypeLabels[CURVE_TYPES.LINEAR];
            const isLastPoint = index === lane.points.length - 1;
            
            return (
              <div 
                key={point.id} 
                className="absolute" 
                style={{ 
                  left: `${xPercent}%`, 
                  top: 0,
                  zIndex: isPointSelected ? 20 : 1,
                }}
              >
                {/* Curve Type Indicator (shown below point, only for selected or hovered) */}
                {isPointSelected && !isLastPoint && (
                  <div 
                    className="absolute text-[8px] font-bold px-1 py-0.5 bg-zinc-800 rounded border border-zinc-600 text-zinc-300 cursor-pointer hover:bg-zinc-700"
                    style={{ 
                      top: y + POINT_RADIUS + 4,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 25,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePointContextMenu(e, point);
                    }}
                    title={`Curve: ${curveInfo.label} (click to change)`}
                  >
                    {curveInfo.icon}
                  </div>
                )}
                
                {/* Point Circle */}
                <motion.div
                  className={`absolute rounded-full cursor-move border-2 ${
                    isPointSelected 
                      ? 'bg-white border-white' 
                      : 'bg-zinc-900 border-current'
                  }`}
                  style={{
                    top: y,
                    left: 0,
                    width: isPointSelected ? POINT_RADIUS * 2 + 4 : POINT_RADIUS * 2,
                    height: isPointSelected ? POINT_RADIUS * 2 + 4 : POINT_RADIUS * 2,
                    marginLeft: isPointSelected ? -(POINT_RADIUS + 2) : -POINT_RADIUS,
                    marginTop: isPointSelected ? -(POINT_RADIUS + 2) : -POINT_RADIUS,
                    borderColor: trackColor || 'rgb(139, 92, 246)',
                  }}
                  onMouseDown={(e) => handlePointMouseDown(e, point)}
                  onDoubleClick={(e) => handlePointDoubleClick(e, point)}
                  onContextMenu={(e) => handlePointContextMenu(e, point)}
                  whileHover={{ scale: 1.2 }}
                  data-testid={`automation-point-${point.id}`}
                  title={`${formatValue(point.value, paramConfig)} @ ${point.time.toFixed(2)}s | Right-click to change curve`}
                />
              </div>
            );
          })}
          
          {/* Playhead Indicator */}
          {isPlaying && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-amber-500 pointer-events-none"
              style={{ left: `${timeToPercent(currentTime)}%` }}
            />
          )}
        </div>
      )}
      
      {/* Collapsed View */}
      {!lane.isVisible && (
        <div className="h-4 bg-zinc-900/50 flex items-center px-2">
          <span className="text-[10px] text-zinc-600">Hidden</span>
        </div>
      )}
    </div>
  );
};

/**
 * Automation Lanes Container for a Track
 */
export const AutomationLanesContainer = ({
  trackId,
  lanes,
  trackColor,
  trackPlugins = [], // Track's plugin chain for parameter discovery
  totalDuration,
  bpm,
  gridSize,
  snapEnabled,
  currentTime,
  isPlaying,
  onLaneUpdate,
  onAddLane,
  onDeleteLane,
  onToggleLaneVisibility,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Check if any lanes are plugin automation
  const hasPluginAutomation = lanes?.some(l => l.parameter?.includes(':'));
  
  if (!lanes || lanes.length === 0) {
    return (
      <div 
        className="flex items-center justify-center py-2 bg-zinc-900/30 border-b border-zinc-800"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onAddLane?.(trackId, 'volume');
          }}
          className="h-6 px-2 text-xs text-zinc-500 hover:text-white gap-1"
          data-testid={`add-automation-${trackId}`}
        >
          <Plus className="w-3 h-3" />
          Add Automation
        </Button>
      </div>
    );
  }
  
  return (
    <div 
      className="border-b border-zinc-700"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Toggle Header */}
      <div 
        className="flex items-center justify-between px-2 py-1 bg-zinc-800/30 cursor-pointer hover:bg-zinc-800/50"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
          <span className="text-xs text-zinc-400">
            Automation ({lanes.length})
            {hasPluginAutomation && <Plug className="w-3 h-3 inline ml-1 text-violet-400" />}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onAddLane?.(trackId, 'volume');
          }}
          className="h-5 px-1 text-xs"
          title="Add Automation Lane"
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      
      {/* Lanes */}
      {isExpanded && lanes.map((lane) => (
        <AutomationLaneView
          key={lane.id}
          lane={lane}
          trackColor={trackColor}
          trackPlugins={trackPlugins}
          totalDuration={totalDuration}
          bpm={bpm}
          gridSize={gridSize}
          snapEnabled={snapEnabled}
          currentTime={currentTime}
          isPlaying={isPlaying}
          onLaneUpdate={onLaneUpdate}
          onDelete={onDeleteLane}
          onToggleVisibility={onToggleLaneVisibility}
        />
      ))}
    </div>
  );
};

export default AutomationLaneView;
